import React, { useState, useEffect } from "react";
import {
  Text,
  Textarea,
  Select,
  TextInput,
  SegmentedControl,
  FileInput,
  Button,
} from "@mantine/core";
import { camelCaseToTitle } from "../../utilities/utilities";
import { apiWrapWithErrorWithData } from "../../utilities/apiHelpers";
import { getuserMail } from "../../utilities/apis/agreements";
import flat from "flat";
import { getUserRolesUsingRoleId } from "../../utilities/apis/serviceProvider";
import { getOrgDetailsFromMaster } from "../../utilities/apis/users";
import { showNotification } from "@mantine/notifications";

export const KYC_FILE_FIELDS = [
  "uploadGST",
  "uploadPAN",
  "uploadAadhaar",
  "uploadOtherKYCDocument",
];

export const hasUpload = (v) => v instanceof File || !!v?.name;

export const getStoredFileMeta = (formValues, fieldPath) => {
  const direct = formValues?.[fieldPath];
  if (direct instanceof File) {
    return {
      file: direct,
      name: direct.name,
      hostedLink: null,
      fileId: null,
    };
  }
  if (direct && typeof direct === "object" && hasUpload(direct)) {
    return {
      file: null,
      name: direct.name,
      hostedLink: direct.hostedLink || null,
      fileId: direct.fileId || null,
    };
  }
  const name = formValues?.[`${fieldPath}.name`];
  if (name) {
    return {
      file: null,
      name,
      hostedLink: formValues[`${fieldPath}.hostedLink`] || null,
      fileId: formValues[`${fieldPath}.fileId`] || null,
    };
  }
  return { file: null, name: null, hostedLink: null, fileId: null };
};

const clearKycField = (values, fieldPath) => {
  const next = { ...values };
  delete next[`${fieldPath}.name`];
  delete next[`${fieldPath}.fileId`];
  delete next[`${fieldPath}.hostedLink`];
  delete next[`${fieldPath}.field`];
  delete next[`${fieldPath}.url`];
  next[fieldPath] = null;
  return next;
};

const AgreementParty = ({
  agreementFormJson,
  partyName,
  dropDownNames,
  setDropDownNames,
  form,
  setName,
  onPartySelect,
  dataRole,
  setDataRole,
  id,
  setAddParty,
  addParty,
}) => {
  if (!agreementFormJson[partyName].type) {
    agreementFormJson[partyName].type = "Entity";
  }
  const [userRolesIdData, setUserRolesIdData] = useState({
    userRoles: {
      SigningAuthority: [],
    },
  });

  const isFirstParty = partyName === "firstParty";
  const registeredName = form.values[`${partyName}.name`] || "";
  const registeredNameOptions = Array.from(
    new Set([...dropDownNames, registeredName].filter(Boolean)),
  );

  useEffect(() => {
    const fetchUserRoles = async () => {
      try {
        const roles = {
          SigningAuthority: (
            await apiWrapWithErrorWithData(getUserRolesUsingRoleId(7))
          ).result,
        };

        setUserRolesIdData((prevData) => ({
          ...prevData,
          userRoles: roles,
        }));
      } catch (error) {
        console.error("Error fetching user roles:", error);
      } finally {
      }
    };

    fetchUserRoles();
  }, []);

  const type = agreementFormJson[partyName].type || "Entity";

  const [orgDetails, setOrgDetails] = useState({
    address: "",
    name: "",
    entityType: "Company",
    brandName: "",
    corporateAddress: "",
    email: "",
    representative: "",
  });
  useEffect(() => {
    const getOrganizationDetails = async () => {
      const resp = await apiWrapWithErrorWithData(getOrgDetailsFromMaster());
      if (resp.success) {
        setOrgDetails({
          address: resp.result[0].details["regAddress"] || "",
          entityType: resp.result[0].details["entityType"] || "",
          name: resp.result[0]["name"] || "",
          brandName: resp.result[0].details?.brandName || "",
        });
      }
    };
    getOrganizationDetails();
  }, []);
  useEffect(() => {
    if (partyName === "firstParty" && orgDetails) {
      form.setValues((prevValues) => {
        const currentValues = flat.unflatten(prevValues);
        // Check if firstParty.name exists
        if (!currentValues.firstParty?.name) {
          const newValues = {
            ...currentValues,
            firstParty: {
              address: orgDetails?.address,
              brandName: orgDetails.brandName,
              corporateAddress: orgDetails.corporateAddress,
              email: orgDetails.signEmail,
              entityType: orgDetails?.entityType,
              name: orgDetails?.name,
              representative: orgDetails?.poc,
              type: "Entity",
            },
          };
          return flat(newValues);
        }

        return prevValues;
      });
    }
  }, [orgDetails]);
  const getMail = async ({ id }) => {
    // Check if id is valid
    if (!id) {
      console.error("getMail: ID is null or undefined");
      return null;
    }

    try {
      const resp = await apiWrapWithErrorWithData(getuserMail({ id }));
      if (resp.success) {
        return resp.email;
      } else {
        console.error("getMail: API returned error:", resp);
        return null;
      }
    } catch (error) {
      console.error("getMail: Exception occurred:", error);
      return null;
    }
  };

  const isDuplicateFileName = (newFile, currentFieldPath) => {
    if (!newFile) return false;
    const newName = newFile.name.toLowerCase();
    const partyKeys = [
      ...new Set(
        Object.keys(form.values)
          .map((key) => key.split(".")[0])
          .filter((key) => key.endsWith("Party")),
      ),
    ];
    return partyKeys.some((partyKey) =>
      KYC_FILE_FIELDS.some((field) => {
        const fieldPath = `${partyKey}.${field}`;
        if (fieldPath === currentFieldPath) return false;
        const meta = getStoredFileMeta(form.values, fieldPath);
        return meta.name && meta.name.toLowerCase() === newName;
      }),
    );
  };

  const handleFileChange = (file, fieldPath) => {
    if (file && isDuplicateFileName(file, fieldPath)) {
      showNotification({
        color: "red",
        title: "Duplicate file",
        message: `"${file.name}" is already uploaded. Please choose a different one.`,
      });
      return;
    }
    form.setValues((prev) => {
      const next = clearKycField(prev, fieldPath);
      next[fieldPath] = file;
      return next;
    });
  };

  const renderKycFileInput = ({
    field,
    label,
    placeholder,
    required = false,
  }) => {
    const fieldPath = `${partyName}.${field}`;
    const meta = getStoredFileMeta(form.values, fieldPath);
    const isRequired = required && !hasUpload(meta.file || { name: meta.name });
    return (
      <div>
        <FileInput
          label={label}
          required={isRequired}
          placeholder={meta.name || placeholder}
          accept="image/*,application/pdf"
          value={meta.file}
          error={form.errors[fieldPath]}
          onChange={(file) => handleFileChange(file, fieldPath)}
        />
        {meta.name && !meta.file && (
          <Text size="xs" color="dimmed" mt={4}>
            {meta.hostedLink ? (
              <a href={meta.hostedLink} target="_blank" rel="noreferrer">
                Current file: {meta.name}
              </a>
            ) : (
              `Current file: ${meta.name}`
            )}
          </Text>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col">
      <div
        className={`flex justify-between items-end border-b-2 border-[#46BDE1] pb-2 ${!isFirstParty ? "mt-8" : "mt-2"} mb-4`}
      >
        <Text className="text-lg" color="#46BDE1">
          {camelCaseToTitle(partyName)}
        </Text>
        <div className="flex items-center gap-2">
          {!isFirstParty && (
            <SegmentedControl
              data={["Entity", "Individual"]}
              {...form.getInputProps(`${partyName}.type`)}
              onChange={(value) => {
                form.setValues((prev) => {
                  let next = { ...prev };
                  KYC_FILE_FIELDS.forEach((field) => {
                    next = clearKycField(next, `${partyName}.${field}`);
                  });
                  next[`${partyName}.type`] = value;
                  next[`${partyName}.gstNo`] = "";
                  next[`${partyName}.panNo`] = "";
                  next[`${partyName}.aadhaarNo`] = "";
                  next[`${partyName}.indi_pan`] = "";
                  return next;
                });

                Object.keys(form.errors).forEach((key) => {
                  if (key.startsWith(`${partyName}.`)) {
                    form.clearFieldError(key);
                  }
                });
              }}
            />
          )}
          {addParty > 2 && (
            <Button
              onClick={() => setAddParty((pre) => pre - 1)}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete Party
            </Button>
          )}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-y-4 gap-x-8">
        {type === "Entity" && (
          <>
            {!isFirstParty && (
              <>
                <Select
                  required
                  label="Party Type"
                  style={{
                    width: "100%",
                  }}
                  data={[
                    { label: "Supplier", value: "Supplier" },
                    { label: "Customer", value: "Customer" },
                  ]}
                  placeholder="Select party Type"
                  {...form.getInputProps(`${partyName}.partyType`)}
                />
                <TextInput
                  label="Party Code (Same as ERP)"
                  required
                  placeholder="enter party code"
                  {...form.getInputProps(`${partyName}.partyCode`)}
                />
              </>
            )}
            {partyName !== "firstParty" ? (
              <Select
                required
                searchable
                creatable
                className="inputCustom "
                label="Legal Name"
                placeholder="enter legal name"
                data={registeredNameOptions}
                getCreateLabel={(query) => `+ add ${query}`}
                onCreate={(query) => {
                  const trimmed = query.trim();
                  if (!trimmed) return query;

                  setDropDownNames((prev) =>
                    prev.includes(trimmed) ? prev : [...prev, trimmed],
                  );
                  form.setFieldValue(`${partyName}.name`, trimmed);
                  onPartySelect?.(partyName, trimmed);

                  return trimmed;
                }}
                {...form.getInputProps(`${partyName}.name`, {
                  withOnChange: false,
                })}
                onChange={(value) => {
                  form.setFieldValue(`${partyName}.name`, value);
                  onPartySelect?.(partyName, value);
                }}
                style={{
                  width: "100%",
                  marginLeft: "0px",
                  margin: "0px",
                  padding: "0px",
                  lineHeight: "1.55",
                }}
              />
            ) : (
              <TextInput
                required
                label="Legal Name"
                placeholder="enter legal name"
                readOnly={isFirstParty}
                {...form.getInputProps(`${partyName}.name`)}
              />
            )}
            <Select
              required={!isFirstParty}
              label="Entity type"
              placeholder="Choose Entity type"
              style={{ width: "100%", marginLeft: "0px", lineHeight: "1.55" }}
              data={[
                { value: "Company", label: "Company" },
                { value: "LLP", label: "LLP" },
                { value: "Partnership Firm", label: "Partnership Firm" },
                { value: "Sole Proprietary", label: "Sole Proprietary" },
              ]}
              disabled={isFirstParty}
              {...form.getInputProps(`${partyName}.entityType`)}
            />

            <TextInput
              required={!isFirstParty}
              label="Brand Name"
              placeholder="enter brand name"
              readOnly={isFirstParty}
              {...form.getInputProps(`${partyName}.brandName`)}
            />
            {partyName === "firstParty" ? (
              <>
                <Select
                  required={!isFirstParty}
                  label="Signing Authority"
                  placeholder="signing authority"
                  value={dataRole["7"]?.[0]?.userId || null}
                  onChange={async (selectedUser) => {
                    setDataRole({
                      ...dataRole,
                      ["7"]: [
                        {
                          userId: selectedUser,
                          isComplete: false,
                        },
                      ],
                    });
                    const selectedUserLabel =
                      userRolesIdData.userRoles.SigningAuthority.find(
                        (user) => user.id === selectedUser,
                      )?.name;
                    form.setFieldValue(
                      `${partyName}.signatory`,
                      selectedUserLabel,
                    );
                    form.setFieldValue(
                      `${partyName}.email`,
                      await getMail({ id: selectedUser }),
                    );
                    // Clear any existing error when a value is selected
                    form.clearFieldError(`${partyName}.signingAuthority`);
                  }}
                  data={userRolesIdData.userRoles.SigningAuthority.map(
                    (user) => ({
                      value: user.id,
                      label: user.name,
                    }),
                  )}
                  required
                  error={form.errors[`${partyName}.signingAuthority`]}
                />
              </>
            ) : (
              <>
                <TextInput
                  required
                  label="Authorised Signatory Name"
                  placeholder="enter authorised signatory name"
                  {...form.getInputProps(`${partyName}.signatory`)}
                />
                <TextInput
                  required
                  label="Authorised Signatory Email"
                  placeholder="enter authorised signatory email"
                  {...form.getInputProps(`${partyName}.email`)}
                />
              </>
            )}
            <Textarea
              required={!isFirstParty}
              label="Registered Address"
              placeholder="enter registered address"
              readOnly={isFirstParty}
              {...form.getInputProps(`${partyName}.address`)}
            />
            <Textarea
              required={!isFirstParty}
              label="Corporate Address"
              placeholder="enter corporate address"
              readOnly={isFirstParty}
              {...form.getInputProps(`${partyName}.corporateAddress`)}
            />
            <TextInput
              required={!isFirstParty}
              label="Business POC"
              placeholder="enter business POC"
              readOnly={isFirstParty}
              {...form.getInputProps(`${partyName}.representative`)}
            />
            <TextInput
              required={!isFirstParty}
              label="Business POC Email"
              placeholder="enter business email"
              readOnly={isFirstParty}
              {...form.getInputProps(`${partyName}.pocemail`)}
            />
            <TextInput
              required={!isFirstParty}
              label="Business POC Phone"
              placeholder="enter business phone"
              readOnly={isFirstParty}
              maxLength={10}
              inputMode="numeric"
              {...form.getInputProps(`${partyName}.pocphone`)}
              onInput={(e) => {
                e.currentTarget.value = e.currentTarget.value.replace(
                  /\D/g,
                  "",
                );
              }}
            />

            {!isFirstParty && (
              <>
                <TextInput
                  label="GSTIN No."
                  placeholder="Enter GSTIN No."
                  maxLength={15}
                  required
                  {...form.getInputProps(`${partyName}.gstNo`)}
                  onInput={(e) => {
                    e.currentTarget.value = e.currentTarget.value
                      .toUpperCase()
                      .replace(/[^A-Z0-9]/g, "");
                  }}
                />
                <TextInput
                  label="PAN No."
                  placeholder="Enter PAN No."
                  maxLength={10}
                  required
                  {...form.getInputProps(`${partyName}.panNo`)}
                  onInput={(e) => {
                    e.currentTarget.value = e.currentTarget.value
                      .toUpperCase()
                      .replace(/[^A-Z0-9]/g, "");
                  }}
                />
                {renderKycFileInput({
                  field: "uploadOtherKYCDocument",
                  label: "Upload Other KYC Document",
                  placeholder: "upload other kyc document",
                })}
                {renderKycFileInput({
                  field: "uploadGST",
                  label: "Upload GST Certificate",
                  placeholder: "upload GST certificate",
                  required: true,
                })}
                {renderKycFileInput({
                  field: "uploadPAN",
                  label: "Upload PAN Card",
                  placeholder: "upload pan card",
                  required: true,
                })}
              </>
            )}
          </>
        )}
        {type === "Individual" && (
          <>
            <TextInput
              required
              label="Individual Name"
              placeholder="enter individual name"
              {...form.getInputProps(`${partyName}.indi_name`)}
            />
            <TextInput
              required
              label="Father's Name"
              placeholder="enter father's name"
              {...form.getInputProps(`${partyName}.indi_fatherName`)}
            />
            <Textarea
              required
              label="Residence Address"
              placeholder="enter residence address"
              {...form.getInputProps(`${partyName}.indi_address`)}
            />
            <TextInput
              required
              label="PAN No."
              placeholder="enter PAN no."
              maxLength={10}
              {...form.getInputProps(`${partyName}.indi_pan`)}
              onInput={(e) => {
                e.currentTarget.value = e.currentTarget.value
                  .toUpperCase()
                  .replace(/[^A-Z0-9]/g, "");
              }}
            />
            <TextInput
              required
              label="Email"
              placeholder="enter email id"
              {...form.getInputProps(`${partyName}.indi_email`)}
            />
            <TextInput
              required
              label="Phone No."
              placeholder="enter 10 digit phone no."
              maxLength={10}
              inputMode="numeric"
              {...form.getInputProps(`${partyName}.indi_phone`)}
              onInput={(e) => {
                e.currentTarget.value = e.currentTarget.value.replace(
                  /\D/g,
                  "",
                );
              }}
            />
            {renderKycFileInput({
              field: "uploadPAN",
              label: "Upload PAN Card",
              placeholder: "upload pan card",
              required: true,
            })}
            <TextInput
              label="Aadhaar Card No."
              placeholder="Enter Aadhaar No."
              maxLength={12}
              inputMode="numeric"
              required
              {...form.getInputProps(`${partyName}.aadhaarNo`)}
              onInput={(e) => {
                e.currentTarget.value = e.currentTarget.value.replace(
                  /\D/g,
                  "",
                );
              }}
            />
            {renderKycFileInput({
              field: "uploadAadhaar",
              label: "Upload Aadhaar Card",
              placeholder: "upload aadhaar card",
              required: true,
            })}
            <TextInput
              required
              label="GSTIN No."
              placeholder="Enter GSTIN No."
              maxLength={15}
              {...form.getInputProps(`${partyName}.gstNo`)}
              onInput={(e) => {
                e.currentTarget.value = e.currentTarget.value
                  .toUpperCase()
                  .replace(/[^A-Z0-9]/g, "");
              }}
            />
            {renderKycFileInput({
              field: "uploadGST",
              label: "Upload GST Certificate",
              placeholder: "upload GST certificate",
              required: true,
            })}
          </>
        )}
      </div>
    </div>
  );
};

export default AgreementParty;
